/**
 * The Ledger tab (TMMRSL-0080): the reseller's own balance, open holds and statements.
 * Every figure is the server's. No holds, voids, manual payments or adjustments.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  ledgerApi,
  isLedgerNotDeployed,
  isStatementNotFound,
  type LedgerLinePage,
  type LedgerLineView,
  type LedgerStatementPage,
  type LedgerStatementView,
  type PartnerLedgerView,
} from '@/api/ledger';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useActionError, type ActionError } from '@/components/useActionError';
import { AccountingAmount } from '@/components/cells';
import { EmptyState } from '@/components/EmptyState';
import { StatusChip } from '@/components/status/StatusChip';
import { statusStyle } from '@/components/status/statusTones';
import { useRemote } from '@/components/useRemote';
import { formatCivilMonthOrDash, formatInstantOrDash, DATE_ONLY } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { lineKindLabel } from './ledgerLabels';

const nowrap = { whiteSpace: 'nowrap' } as const;

interface Page<T> {
  items: T[];
  hasMore: boolean;
  nextAfterId: string | undefined;
}

function pageOf<T>(
  items: readonly T[] | undefined,
  hasMore: boolean | undefined,
  nextAfterId: string | undefined,
): Page<T> {
  return { items: [...(items ?? [])], hasMore: hasMore === true, nextAfterId };
}

/** The first page, plus further pages appended by Load more (`afterId = nextAfterId`). */
function useMore<T>(first: Page<T> | null, fetchMore: (afterId: string) => Promise<Page<T>>) {
  const describe = useActionError();
  const [stamp, setStamp] = useState(first);
  const [extra, setExtra] = useState<T[]>([]);
  const [tail, setTail] = useState<Pick<Page<T>, 'hasMore' | 'nextAfterId'> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  if (stamp !== first) {
    setStamp(first);
    setExtra([]);
    setTail(null);
    setBusy(false);
    setError(null);
  }
  const current = tail ?? (first ? { hasMore: first.hasMore, nextAfterId: first.nextAfterId } : null);
  const nextAfterId = current?.nextAfterId;
  const hasMore = current?.hasMore === true && nextAfterId !== undefined;

  const loadMore = useCallback(async () => {
    if (!nextAfterId) return;
    setBusy(true);
    try {
      const page = await fetchMore(nextAfterId);
      setExtra((rows) => [...rows, ...page.items]);
      setTail({ hasMore: page.hasMore, nextAfterId: page.nextAfterId });
      setError(null);
    } catch (err) {
      // The rows already shown stay. Retry replaces Load more until this succeeds.
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }, [describe, fetchMore, nextAfterId]);

  return { items: [...(first?.items ?? []), ...extra], hasMore, loadMore, busy, error };
}

function LedgerMoney({
  minor,
  currency,
}: {
  minor: number | undefined;
  currency: string | undefined;
}) {
  const { t } = useTranslation();
  if (minor === undefined || !currency) return t('statements.unavailable');
  return <AccountingAmount minor={minor} currency={currency} />;
}

function MoneyCell({
  minor,
  currency,
}: {
  minor: number | undefined;
  currency: string | undefined;
}) {
  return (
    <TableCell align="right" sx={nowrap}>
      <LedgerMoney minor={minor} currency={currency} />
    </TableCell>
  );
}

/** A statement status. An unknown value keeps its raw status and shows the generic label. */
function LedgerStatusChip({ status }: { status: string | undefined }) {
  const known = statusStyle('ledgerStatement', status) !== null;
  return <StatusChip domain="ledgerStatement" status={status && !known ? 'OTHER' : status} />;
}

function HoldList({ view }: { view: PartnerLedgerView }) {
  const { t } = useTranslation();
  const holds = view.openHolds;
  if (holds.length === 0) return null;
  return (
    <Stack spacing={0.75}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('ledger.holds')}
      </Typography>
      <Box
        component="ul"
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, listStyle: 'none', m: 0, p: 0 }}
      >
        {holds.map((hold) => (
          <Box
            component="li"
            key={hold.holdId || hold.kind}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
          >
            <StatusChip
              domain="ledgerHold"
              status={hold.kind}
              {...(hold.kind === 'OTHER' ? { values: { kind: hold.rawKind ?? t('statements.unavailable') } } : {})}
            />
            <LedgerMoney minor={hold.amountMinor} currency={view.currency} />
            {hold.overCap ? (
              <Typography variant="body2" component="span">
                {t('ledger.overCap')}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function Summary({ view }: { view: PartnerLedgerView }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const last = view.lastStatement;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('ledger.balance')}
      </Typography>
      <Typography variant="h2" component="p" sx={nowrap}>
        <LedgerMoney minor={view.balanceMinor} currency={view.currency} />
      </Typography>
      <HoldList view={view} />
      <Typography variant="body2" component="div">
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {t('ledger.last')}{' '}
        </Box>
        {last ? (
          <>
            {formatCivilMonthOrDash(last.periodMonth, locale, dash)}{' '}
            <LedgerStatusChip status={last.status} />
          </>
        ) : (
          dash
        )}
      </Typography>
    </Stack>
  );
}

function LoadMore({
  shown,
  busy,
  error,
  onClick,
}: {
  shown: boolean;
  busy: boolean;
  error: ActionError | null;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ActionErrorAlert
        error={error}
        action={
          <Button color="inherit" size="small" onClick={onClick} disabled={busy}>
            {t('common.retry')}
          </Button>
        }
      />
      {shown && error === null ? (
        <Button onClick={onClick} disabled={busy}>
          {t('ledger.loadMore')}
        </Button>
      ) : null}
    </>
  );
}

function LineTable({
  lines,
  label,
  empty,
}: {
  lines: LedgerLineView[];
  label: string;
  empty: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  if (lines.length === 0) {
    return <Typography role="status">{empty}</Typography>;
  }
  return (
    <Paper sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell>{t('ledger.lines.date')}</TableCell>
            <TableCell>{t('ledger.lines.description')}</TableCell>
            <TableCell align="right">{t('ledger.lines.amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.lineId || `${line.createdAt}-${line.kind}`}>
              <TableCell sx={nowrap}>{formatInstantOrDash(line.createdAt, locale, dash, DATE_ONLY)}</TableCell>
              <TableCell>
                <Typography variant="body2">{lineKindLabel(line.kind ?? '', t)}</Typography>
                {line.voided ? (
                  <Typography variant="body2" component="div">
                    {t('ledger.lines.voided')}
                  </Typography>
                ) : null}
                {line.description ? (
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {line.description}
                  </Typography>
                ) : null}
                {line.reason ? (
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {line.reason}
                  </Typography>
                ) : null}
                {line.reversesLineId ? (
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {t('ledger.lines.reverses', { id: line.reversesLineId })}
                  </Typography>
                ) : null}
              </TableCell>
              <MoneyCell minor={line.amountMinor} currency={line.currency} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function SelectedStatement({
  statementId,
  earlierSearch,
  onClose,
  onGone,
}: {
  statementId: string;
  earlierSearch: string;
  onClose: () => void;
  onGone: (id: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const gone = useRef(onGone);
  gone.current = onGone;
  const load = useCallback(async (): Promise<{
    statement: LedgerStatementView | null;
    lines: LedgerLinePage;
    notDeployed: boolean;
  }> => {
    const empty: LedgerLinePage = { lines: [], hasMore: false };
    try {
      const [statement, lines] = await Promise.all([
        ledgerApi.getStatement(statementId),
        ledgerApi.listStatementLines(statementId),
      ]);
      return { statement, lines, notDeployed: false };
    } catch (err) {
      if (isStatementNotFound(err)) {
        gone.current(statementId);
        return { statement: null, lines: empty, notDeployed: false };
      }
      if (isLedgerNotDeployed(err)) {
        return { statement: null, lines: empty, notDeployed: true };
      }
      throw err;
    }
  }, [statementId]);
  const remote = useRemote(load);
  const first = useMemo(
    () =>
      remote.data
        ? pageOf(remote.data.lines.lines, remote.data.lines.hasMore, remote.data.lines.nextAfterId)
        : null,
    [remote.data],
  );
  const fetchMore = useCallback(
    (afterId: string) =>
      ledgerApi.listStatementLines(statementId, afterId).then((page) =>
        pageOf(page.lines, page.hasMore, page.nextAfterId),
      ),
    [statementId],
  );
  const more = useMore(first, fetchMore);

  if (remote.status === 'error') {
    return (
      <ApiErrorState
        what={t('errors.what.ledgerStatement')}
        error={remote.error}
        onRetry={remote.reload}
      />
    );
  }
  if (!remote.data) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={120} />
      </Box>
    );
  }
  if (remote.data.notDeployed) {
    return <LedgerNotDeployed earlierSearch={earlierSearch} />;
  }
  if (!remote.data.statement) return null;
  const period = formatCivilMonthOrDash(remote.data.statement.periodMonth, locale, dash);
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="h3" component="h3">
          {t('ledger.statementTitle', { period })}
        </Typography>
        <Button onClick={onClose}>{t('ledger.close')}</Button>
      </Stack>
      <LineTable lines={more.items} label={t('ledger.statementTitle', { period })} empty={t('ledger.lines.empty')} />
      <LoadMore
        shown={more.hasMore}
        busy={more.busy}
        error={more.error}
        onClick={() => void more.loadMore()}
      />
    </Stack>
  );
}

function StatementSection({
  selectedId,
  earlierSearch,
  openSearch,
  linkState,
  onClose,
  onGone,
}: {
  selectedId: string;
  earlierSearch: string;
  openSearch: (id: string) => string;
  linkState: unknown;
  onClose: () => void;
  onGone: (id: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const dash = t('statements.unavailable');
  const load = useCallback(() => ledgerApi.listStatements(), []);
  const remote = useRemote(load);
  const first = useMemo(
    () =>
      remote.data
        ? pageOf(remote.data.statements, remote.data.hasMore, remote.data.nextAfterId)
        : null,
    [remote.data],
  );
  const fetchMore = useCallback(
    (afterId: string) =>
      ledgerApi.listStatements(afterId).then((page: LedgerStatementPage) =>
        pageOf(page.statements, page.hasMore, page.nextAfterId),
      ),
    [],
  );
  const more = useMore(first, fetchMore);

  if (remote.status === 'error') {
    if (isLedgerNotDeployed(remote.error)) {
      return <LedgerNotDeployed earlierSearch={earlierSearch} />;
    }
    return (
      <ApiErrorState what={t('errors.what.ledgerStatement')} error={remote.error} onRetry={remote.reload} />
    );
  }
  if (!remote.data) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={160} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography
        id="ledger-statements-heading"
        variant="h2"
        component="h2"
        tabIndex={-1}
        sx={{ '&:focus:not(:focus-visible)': { outline: 'none' } }}
      >
        {t('ledger.statements')}
      </Typography>
      {more.items.length === 0 ? (
        <Typography role="status">{t('ledger.statementsEmpty')}</Typography>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('ledger.statements')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('ledger.col.period')}</TableCell>
                <TableCell>{t('ledger.col.status')}</TableCell>
                <TableCell align="right">{t('ledger.col.opening')}</TableCell>
                <TableCell align="right">{t('ledger.col.lines')}</TableCell>
                <TableCell align="right">{t('ledger.col.closing')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {more.items.map((statement) => (
                <StatementRow
                  key={statement.statementId || statement.periodMonth}
                  statement={statement}
                  selected={statement.statementId !== '' && statement.statementId === selectedId}
                  period={formatCivilMonthOrDash(statement.periodMonth, locale, dash)}
                  openSearch={openSearch}
                  linkState={linkState}
                  selectedBg={tokens.brandSoft}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      <LoadMore
        shown={more.hasMore}
        busy={more.busy}
        error={more.error}
        onClick={() => void more.loadMore()}
      />
      {selectedId ? (
        <SelectedStatement
          statementId={selectedId}
          earlierSearch={earlierSearch}
          onClose={onClose}
          onGone={onGone}
        />
      ) : null}
    </Stack>
  );
}

function StatementRow({
  statement,
  selected,
  period,
  openSearch,
  linkState,
  selectedBg,
}: {
  statement: LedgerStatementView;
  selected: boolean;
  period: string;
  openSearch: (id: string) => string;
  linkState: unknown;
  selectedBg: string;
}) {
  const id = statement.statementId;
  return (
    <TableRow selected={selected} hover sx={{ bgcolor: selected ? selectedBg : undefined }}>
      <TableCell component="th" scope="row">
        {!id ? (
          period
        ) : (
          <Link
            id={`ledger-statement-${id}`}
            component={RouterLink}
            to={{ search: openSearch(id) }}
            state={linkState}
            aria-current={selected ? 'true' : undefined}
            onClick={(event) => {
              if (selected) event.preventDefault();
            }}
          >
            {period}
          </Link>
        )}
      </TableCell>
      <TableCell>
        <LedgerStatusChip status={statement.status} />
      </TableCell>
      <MoneyCell minor={statement.openingMinor} currency={statement.currency} />
      <MoneyCell minor={statement.linesMinor} currency={statement.currency} />
      <MoneyCell minor={statement.closingMinor} currency={statement.currency} />
    </TableRow>
  );
}

function UnassignedSection() {
  const { t } = useTranslation();
  const load = useCallback(() => ledgerApi.listUnassignedLines(), []);
  const remote = useRemote(load);
  const first = useMemo(
    () =>
      remote.data ? pageOf(remote.data.lines, remote.data.hasMore, remote.data.nextAfterId) : null,
    [remote.data],
  );
  const fetchMore = useCallback(
    (afterId: string) =>
      ledgerApi.listUnassignedLines(afterId).then((page) => pageOf(page.lines, page.hasMore, page.nextAfterId)),
    [],
  );
  const more = useMore(first, fetchMore);

  if (remote.status === 'error') {
    if (isLedgerNotDeployed(remote.error)) return null;
    return <ApiErrorState what={t('errors.what.ledgerStatement')} error={remote.error} onRetry={remote.reload} />;
  }
  if (!remote.data) return null;
  if (more.items.length === 0 && !more.hasMore) return null;
  return (
    <Stack spacing={1.5}>
      <Typography variant="h2" component="h2">
        {t('ledger.unassigned')}
      </Typography>
      <LineTable lines={more.items} label={t('ledger.unassigned')} empty={t('ledger.lines.empty')} />
      <LoadMore
        shown={more.hasMore}
        busy={more.busy}
        error={more.error}
        onClick={() => void more.loadMore()}
      />
    </Stack>
  );
}

export function LedgerTab({
  view,
  statementId,
  earlierSearch,
  openSearch,
  linkState,
  onClose,
  onGone,
}: {
  view: PartnerLedgerView;
  statementId: string;
  earlierSearch: string;
  openSearch: (id: string) => string;
  linkState: unknown;
  onClose: () => void;
  onGone: (id: string) => void;
}) {
  return (
    <Stack spacing={2.5}>
      <Summary view={view} />
      <StatementSection
        selectedId={statementId}
        earlierSearch={earlierSearch}
        openSearch={openSearch}
        linkState={linkState}
        onClose={onClose}
        onGone={onGone}
      />
      <UnassignedSection />
    </Stack>
  );
}

/** No ledger yet: nothing in use, or no payee. A link opens Earlier settlements. */
export function NoLedgerYet({ earlierSearch }: { earlierSearch: string }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      kind="none"
      title={t('ledger.empty')}
      action={
        <Link component={RouterLink} to={{ search: earlierSearch }}>
          {t('ledger.earlierLink')}
        </Link>
      }
    />
  );
}

/** Payouts have moved, and the ledger has no balance, hold, or statement yet. */
export function RetiredEmptyLedger() {
  const { t } = useTranslation();
  return (
    <Stack spacing={2.5}>
      <Typography role="status">{t('ledger.emptyRetired')}</Typography>
      <UnassignedSection />
    </Stack>
  );
}

/** The ledger route is not deployed. One sentence, so the link can sit anywhere in it. */
export function LedgerNotDeployed({ earlierSearch }: { earlierSearch: string }) {
  return (
    <Typography role="status">
      <Trans
        i18nKey="ledger.notDeployed"
        components={{
          earlier: <Link component={RouterLink} to={{ search: earlierSearch }} />,
        }}
      />
    </Typography>
  );
}
